import React, { useState, useEffect } from 'react';
import { 
  PhoneCall, Users, Clock, CheckCircle, 
  AlertCircle, MessageSquare, TrendingUp,
  Calendar, Filter, RefreshCw
} from 'lucide-react';
import api from '../../services/api';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { authStore } from '../../stores/authStore';
import toast from 'react-hot-toast';

const AtendenteDashboard = () => {
  const navigate = useNavigate();
  const { user } = authStore();
  const [loading, setLoading] = useState(true);
  const [atendimentos, setAtendimentos] = useState([]);
  const [chamados, setChamados] = useState([]);
  const [estatisticas, setEstatisticas] = useState(null);
  const [clientesRecentes, setClientesRecentes] = useState([]);
  const [filtros, setFiltros] = useState({
    periodo: 'hoje',
    status: 'todos'
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Busca dados relevantes para atendente
      const hoje = format(new Date(), 'yyyy-MM-dd');
      const mes = new Date().getMonth() + 1;
      const ano = new Date().getFullYear();
      
      const [atendimentosRes, chamadosRes, estatisticasRes] = await Promise.all([
        api.get(`/atendimentos?page=1&sortBy=dataHora&sortOrder=DESC`),
        api.get('/chamados/abertos'),
        api.get(`/atendimentos/estatisticas/${mes}/${ano}`)
      ]);

      setAtendimentos(atendimentosRes.data.data || []);
      setChamados(chamadosRes.data || []);
      setEstatisticas(estatisticasRes.data);

      // Busca clientes dos atendimentos recentes
      const clienteIds = [...new Set(atendimentosRes.data.data?.map(a => a.clienteId) || [])];
      if (clienteIds.length > 0) {
        const clientesPromises = clienteIds.slice(0, 5).map(id => 
          api.get(`/clientes/${id}`).catch(() => null)
        );
        const clientesData = await Promise.all(clientesPromises);
        setClientesRecentes(clientesData.filter(c => c?.data).map(c => c.data));
      }

    } catch (error) {
      toast.error('Erro ao carregar dados do dashboard');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000); // Atualiza a cada minuto
    return () => clearInterval(interval);
  }, []);

  const iniciarAtendimento = () => {
    navigate('/atendimentos/novo');
  };

  const abrirChamado = () => {
    navigate('/chamados/novo');
  };

  const verDetalhesCliente = (clienteId) => {
    navigate(`/clientes/${clienteId}/detalhes`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Dashboard de Atendimento
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Olá, {user?.nome}! Você tem {chamados.length} chamados aguardando.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={iniciarAtendimento}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <PhoneCall className="w-4 h-4" />
            Novo Atendimento
          </button>
          <button
            onClick={abrirChamado}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
          >
            <AlertCircle className="w-4 h-4" />
            Abrir Chamado
          </button>
        </div>
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Atendimentos Hoje</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {estatisticas?.totalAtendimentos || 0}
              </p>
            </div>
            <PhoneCall className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Tempo Médio</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {estatisticas?.tempoMedioMinutos || '0'} min
              </p>
            </div>
            <Clock className="w-8 h-8 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Chamados Abertos</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {chamados.length}
              </p>
            </div>
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Por Canal</p>
              <div className="flex gap-2 mt-1">
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                  Tel: {estatisticas?.porCanal?.telefone || 0}
                </span>
                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                  WhatsApp: {estatisticas?.porCanal?.whatsapp || 0}
                </span>
              </div>
            </div>
            <MessageSquare className="w-8 h-8 text-green-500" />
          </div>
        </div>
      </div>

      {/* Duas colunas principais */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Atendimentos Recentes */}
        <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Atendimentos Recentes
            </h2>
          </div>
          <div className="p-6">
            {atendimentos.length > 0 ? (
              <div className="space-y-4">
                {atendimentos.slice(0, 5).map((atendimento) => (
                  <div 
                    key={atendimento.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                    onClick={() => verDetalhesCliente(atendimento.clienteId)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${
                        atendimento.canal === 'telefone' ? 'bg-blue-500' :
                        atendimento.canal === 'whatsapp' ? 'bg-green-500' :
                        atendimento.canal === 'email' ? 'bg-yellow-500' :
                        'bg-gray-500'
                      }`} />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {atendimento.cliente?.nome || 'Cliente'}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {atendimento.tipo?.nome} - {atendimento.duracaoMinutos} min
                        </p>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500">
                      {format(new Date(atendimento.dataHora), 'HH:mm')}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">
                Nenhum atendimento recente
              </p>
            )}
          </div>
        </div>

        {/* Chamados Prioritários */}
        <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Chamados Prioritários
            </h2>
          </div>
          <div className="p-6">
            {chamados.length > 0 ? (
              <div className="space-y-4">
                {chamados
                  .filter(c => c.prioridade === 'alta' || c.prioridade === 'urgente')
                  .slice(0, 5)
                  .map((chamado) => (
                    <div 
                      key={chamado.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                      onClick={() => navigate(`/chamados/${chamado.id}`)}
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900 dark:text-white">
                            #{chamado.protocolo}
                          </p>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            chamado.prioridade === 'urgente' 
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {chamado.prioridade}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {chamado.titulo}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {chamado.cliente?.nome}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">
                          Prazo: {format(new Date(chamado.prazo), 'dd/MM HH:mm')}
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">
                Nenhum chamado prioritário
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Clientes Recentes */}
      <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Clientes Atendidos Recentemente
          </h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {clientesRecentes.map((cliente) => (
              <div
                key={cliente.id}
                onClick={() => verDetalhesCliente(cliente.id)}
                className="p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer text-center"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Users className="w-6 h-6 text-primary-600" />
                </div>
                <p className="font-medium text-gray-900 dark:text-white text-sm">
                  {cliente.nome}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {cliente.telefones?.[0]?.numero || 'Sem telefone'}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AtendenteDashboard;